import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { Locale } from '@hitster/shared';
import { locales, type TranslationKey } from './locales.ts';
import { readSetting, writeSetting } from './storage.ts';

/**
 * Sehr kleiner i18n-Layer. Ausreichend, weil die App nur zwei Sprachen
 * kennt und keine Pluralregeln oder Datumsformate braucht.
 */

export type TranslateParams = Record<string, string | number>;
export type Translate = (key: TranslationKey, params?: TranslateParams) => string;

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: Translate;
}

const I18nContext = createContext<I18nContextValue | null>(null);

const STORAGE_KEY = 'hitster.locale';

/**
 * Sprache aus localStorage, sonst aus den Browser-Einstellungen.
 *
 * Der Zugriff ist abgesichert: in Safaris privatem Modus und bei
 * blockierten Cookies wirft `localStorage` eine Ausnahme oder fehlt
 * ganz. Die Sprachwahl darf daran nicht die ganze App verhindern.
 */
function detectLocale(): Locale {
  const stored = readSetting(STORAGE_KEY);
  if (stored === 'de' || stored === 'en') return stored;
  return navigator.language?.toLowerCase().startsWith('de') ? 'de' : 'en';
}

/** Ersetzt `{name}`-Platzhalter. */
function interpolate(template: string, params?: TranslateParams): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (match, key: string) => {
    const value = params[key];
    return value === undefined ? match : String(value);
  });
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [locale, setLocaleState] = useState<Locale>(detectLocale);

  const setLocale = useCallback((next: Locale) => {
    writeSetting(STORAGE_KEY, next);
    document.documentElement.lang = next;
    setLocaleState(next);
  }, []);

  const t = useCallback<Translate>(
    (key, params) => {
      const table = locales[locale];
      // Fehlt ein Key in der Uebersetzung, zeigen wir Deutsch statt eines
      // rohen Keys -- weniger irritierend fuer Spieler.
      const template = table[key] ?? locales.de[key] ?? key;
      return interpolate(template, params);
    },
    [locale],
  );

  const value = useMemo(() => ({ locale, setLocale, t }), [locale, setLocale, t]);

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const context = useContext(I18nContext);
  if (!context) throw new Error('useI18n muss innerhalb von I18nProvider verwendet werden');
  return context;
}

/** Kurzform, wenn nur die Uebersetzungsfunktion gebraucht wird. */
export function useT(): Translate {
  return useI18n().t;
}

/**
 * Uebersetzt einen Log-/Fehler-Key, der vom Server kommt.
 * Der Server kennt nur Keys, keine Texte -- so bleibt die Sprache
 * pro Client waehlbar.
 */
export function translateServerKey(
  t: Translate,
  key: string,
  params?: TranslateParams,
): string {
  return t(key as TranslationKey, params);
}
